import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    CodeBlock,
    CodeBlockBody,
    CodeBlockFooter,
    CodeBlockLine,
    CodeBlockToolbar,
    clipboard,
    DEFAULT_SYNTAX,
    isAvailableLanguage,
    tokenizer,
    useSyntaxHighlighter,
} from '@gitbook/spine-react';
import { isScrollableOverflowValue } from '@gitbook/util-dom';

interface OpenAPIPlainCodeBlockProps {
    className?: string;
    code: string;
    syntax?: string;
    showCopyButton?: boolean;
    showToolbar?: boolean;
    overflow?: 'scroll' | 'wrap';
}

const CODE_BLOCK_LINE_HEIGHT = 24;
const CODE_BLOCK_VIRTUALIZATION_THRESHOLD = 50;

/**
 * Read-only code block for OpenAPI surfaces.
 */
export function OpenAPIPlainCodeBlock(props: OpenAPIPlainCodeBlockProps): React.ReactElement {
    const {
        className,
        code,
        syntax,
        showCopyButton = true,
        showToolbar = false,
        overflow = 'scroll',
    } = props;
    const highlighter = useSyntaxHighlighter();
    const resolvedSyntax = isAvailableLanguage(syntax) ? syntax : DEFAULT_SYNTAX;
    const plainLines = React.useMemo(() => code.split('\n'), [code]);
    const codeBlockRef = React.useRef<HTMLDivElement>(null);
    const [isSyntaxReady, setIsSyntaxReady] = React.useState(false);
    const [virtualizationScrollElement, setVirtualizationScrollElement] =
        React.useState<HTMLElement | null>(null);

    React.useEffect(() => {
        let isCancelled = false;

        async function loadSyntax() {
            if (!highlighter) {
                return;
            }

            try {
                if (!highlighter.getLoadedLanguages().includes(resolvedSyntax)) {
                    await highlighter.loadLanguage(resolvedSyntax);
                }
            } catch (_) {
                // Fallback to plain tokenization if the syntax cannot be loaded.
            }

            if (!isCancelled) {
                setIsSyntaxReady(true);
            }
        }

        setIsSyntaxReady(false);
        void loadSyntax();

        return () => {
            isCancelled = true;
        };
    }, [highlighter, resolvedSyntax]);

    const highlightedResult = React.useMemo(() => {
        if (!highlighter || !isSyntaxReady) {
            return null;
        }
        return tokenizer(highlighter, code, resolvedSyntax);
    }, [code, highlighter, resolvedSyntax, isSyntaxReady]);

    // Resolve the actual element that owns scrolling inside the code block.
    const resolveVirtualizationScrollElement = React.useCallback((): HTMLElement | null => {
        if (typeof window === 'undefined') {
            return null;
        }

        const codeBlockElement = codeBlockRef.current;
        if (!codeBlockElement) {
            return null;
        }

        const candidates: HTMLElement[] = [codeBlockElement];
        const codeBlockBody = codeBlockElement.firstElementChild;
        if (codeBlockBody instanceof HTMLElement) {
            candidates.push(codeBlockBody);
        }

        return (
            candidates.find((element) => {
                const overflowY = window.getComputedStyle(element).overflowY;
                if (!isScrollableOverflowValue(overflowY)) {
                    return false;
                }

                return element.scrollHeight > element.clientHeight + 1;
            }) ?? null
        );
    }, []);

    React.useEffect(() => {
        if (overflow !== 'scroll' || plainLines.length <= CODE_BLOCK_VIRTUALIZATION_THRESHOLD) {
            setVirtualizationScrollElement(null);
            return;
        }

        const codeBlockElement = codeBlockRef.current;
        if (!codeBlockElement) {
            setVirtualizationScrollElement(null);
            return;
        }

        const updateScrollElement = () => {
            setVirtualizationScrollElement(resolveVirtualizationScrollElement());
        };

        updateScrollElement();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const resizeObserver = new ResizeObserver(updateScrollElement);
        resizeObserver.observe(codeBlockElement);
        const codeBlockBody = codeBlockElement.firstElementChild;
        if (codeBlockBody instanceof HTMLElement) {
            resizeObserver.observe(codeBlockBody);
            const codeBlockContent = codeBlockBody.firstElementChild;
            if (codeBlockContent instanceof HTMLElement) {
                resizeObserver.observe(codeBlockContent);
            }
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [overflow, plainLines.length, resolveVirtualizationScrollElement]);

    const lines = highlightedResult?.tokens ?? plainLines;
    const shouldVirtualize =
        overflow === 'scroll' &&
        lines.length > CODE_BLOCK_VIRTUALIZATION_THRESHOLD &&
        Boolean(virtualizationScrollElement);

    const virtualizer = useVirtualizer({
        count: lines.length,
        getScrollElement: () => virtualizationScrollElement,
        estimateSize: () => CODE_BLOCK_LINE_HEIGHT,
        overscan: 20,
        enabled: shouldVirtualize,
    });
    const virtualItems = virtualizer.getVirtualItems();
    const useVirtualizedItems = shouldVirtualize && virtualItems.length > 0;

    const topSpacer = useVirtualizedItems ? (virtualItems[0]?.start ?? 0) : 0;
    const bottomSpacer = useVirtualizedItems
        ? Math.max(
              0,
              virtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end ?? 0)
          )
        : 0;

    return (
        <CodeBlock
            ref={codeBlockRef}
            className={clsx('openapi-codeblock', className)}
            overflow={overflow}
        >
            <CodeBlockBody>
                {topSpacer > 0 ? (
                    <div aria-hidden className="col-span-2" style={{ height: topSpacer }} />
                ) : null}

                {useVirtualizedItems
                    ? virtualItems.map((item) => {
                          const lineIndex = item.index;
                          return renderLine({
                              lineIndex,
                              line: lines[lineIndex],
                              isHighlighted: Boolean(highlightedResult),
                          });
                      })
                    : lines.map((line, lineIndex) =>
                          renderLine({
                              lineIndex,
                              line,
                              isHighlighted: Boolean(highlightedResult),
                          })
                      )}

                {bottomSpacer > 0 ? (
                    <div aria-hidden className="col-span-2" style={{ height: bottomSpacer }} />
                ) : null}
            </CodeBlockBody>

            {showCopyButton ? (
                <CodeBlockToolbar show={showToolbar}>
                    <Button
                        label="Copy"
                        leadingIcon={Icon.Copy}
                        size="small"
                        kind="secondary"
                        iconOnly
                        onPress={() => clipboard.writeText(code)}
                    />
                </CodeBlockToolbar>
            ) : null}

            {highlightedResult?.isPartiallyHighlighted ? (
                <CodeBlockFooter className="px-2 pt-2 text-ui-small text-muted">
                    Syntax highlighting is partially disabled for very long lines to keep rendering
                    fast.
                </CodeBlockFooter>
            ) : null}
        </CodeBlock>
    );
}

function renderLine(props: {
    lineIndex: number;
    line:
        | string
        | Array<{
              color?: string;
              content: string;
              fontStyle?: number;
          }>;
    isHighlighted: boolean;
}) {
    const { lineIndex, line, isHighlighted } = props;
    if (isHighlighted && Array.isArray(line)) {
        return (
            <CodeBlockLine
                key={lineIndex}
                number={lineIndex + 1}
                tokens={line.map((token) => ({
                    color: token.color ?? 'var(--shiki-foreground)',
                    content: token.content,
                    fontStyle: token.fontStyle,
                }))}
            />
        );
    }

    return (
        <CodeBlockLine key={lineIndex} number={lineIndex + 1}>
            {String(line) || '\u200B'}
        </CodeBlockLine>
    );
}
